import axios, { AxiosError, AxiosResponse } from 'axios';
import { useMutation, UseMutationResult } from 'react-query';
import { useMutation as useMutationTanstack } from '@tanstack/react-query';

import { LoginRequestBody } from 'api/types';
import routes from 'routes';

export const useLogin = () =>
  useMutationTanstack({
    mutationFn: ({ email, password }: LoginRequestBody) =>
      axios.post(routes.login, { email, password, user_agent: window.navigator.userAgent }),
  });

export const useLogout = () =>
  useMutationTanstack({
    mutationFn: () => axios.post(routes.logout, {}),
  });

export type ChangePasswordRequestBody = {
  old_password: string;
  new_password: string;
};

export function changePassword(body: ChangePasswordRequestBody) {
  return axios.post(routes.changePassword, { ...body });
}

export const useChangePassword = () =>
  useMutation((body: ChangePasswordRequestBody) => axios.post(routes.changePassword, body), {});

type UseSendAccessCodeBody = {
  onSuccess?: (data: AxiosResponse<any, any>, variables: { email: string }) => void;
  onError?: (error: AxiosError) => void;
};

export const useSendAccessCode = ({ onSuccess, onError }: UseSendAccessCodeBody) =>
  useMutation((body: { email: string }) => axios.post(routes.sendAccessCode, body), {
    onSuccess,
    onError,
  });

type VerifyAccessCodeRequestBody = {
  email: string;
  access_code: string;
  is_MFA?: boolean;
};

type UseVerifyAccessCodeBody = {
  onSuccess?: (data: AxiosResponse<any, any>, variables: VerifyAccessCodeRequestBody) => void;
  onError?: (error: AxiosError) => void;
};

export const useVerifyAccessCode = ({ onSuccess, onError }: UseVerifyAccessCodeBody) =>
  useMutation(
    (body: VerifyAccessCodeRequestBody) =>
      axios.post(routes.verifyAccessCode, { ...body, user_agent: window.navigator.userAgent }),
    {
      onSuccess,
      onError,
    },
  );

type UseVerifyMobileTokenBody = {
  onSuccess?: (response: AxiosResponse) => void;
  onError?: (error: AxiosError) => void;
};

export const useVerifyMobileToken = ({
  onSuccess,
  onError,
}: UseVerifyMobileTokenBody): UseMutationResult<AxiosResponse, AxiosError, { token: string }> =>
  useMutation(
    ({ token }: { token: string }) =>
      axios.post(routes.verifyMobileSSOToken, { token }).then((res) => res),
    { onSuccess, onError },
  );
